import { oneDriveRefKey } from '@/features/onedrive/onedrive-attachment.js';
import type {
  ArtifactAttachmentRef,
  OneDriveAttachmentRef,
} from '@/integration/types.js';

/**
 * A workspace file an automation carries, as it travels in `payload.attachments`.
 *
 * Only `artifact_id` means anything when the automation fires — the service resolves the storage key
 * from it, under the visibility of the automation's owner. The other three exist so the window and the
 * chat can draw a chip without asking anyone what the file is called.
 */
export interface AutomationArtifactAttachment {
  artifact_id: string;
  name: string;
  content_type: string;
  size: number;
}

/**
 * A file that stays in the author's personal OneDrive, as it travels in the same list.
 *
 * Nothing is copied: the pair `scope_id` + `item_id` is a live address, and the agent reads the file
 * itself on every firing. So there is no artifact behind it and no storage key to resolve — which is
 * exactly why it is a form of its own rather than a flag on the workspace one. The labels are display
 * only; the service rewrites them from what the cloud's owner answers.
 */
export interface AutomationOneDriveAttachment {
  source: 'onedrive';
  scope_id: string;
  item_id: string;
  name: string;
  content_type: string | null;
  size: number;
}

/** A file an automation carries, whichever of the two places it lives in. */
export type AutomationAttachment =
  AutomationArtifactAttachment | AutomationOneDriveAttachment;

/** True for the live reference into someone's own OneDrive. */
export function isOneDriveAutomationAttachment(
  file: AutomationAttachment,
): file is AutomationOneDriveAttachment {
  return 'source' in file;
}

/**
 * What makes two attached files the same file — the identity a dedup and a chip's key are read from.
 * A workspace file is its artifact; a personal one is the folder AND the item, since an item id means
 * nothing outside the folder it was read in.
 */
export function automationAttachmentKey(file: AutomationAttachment): string {
  return isOneDriveAutomationAttachment(file)
    ? `onedrive\u0000${oneDriveRefKey(file)}`
    : `artifact\u0000${file.artifact_id}`;
}

/** The only capability whose run reads files: nothing else on the other side would ever open one. */
export const AGENT_CHAT_CAPABILITY = 'agent-chat';

/**
 * How many files one automation carries. The same cap the composer puts on a message, and the same one
 * the artifact owner enforces on a resolve — it answers 400 above ten references, which would lose the
 * whole batch, so the cut happens here instead.
 */
export const MAX_AUTOMATION_ATTACHMENTS = 10;

/**
 * Turn a file the picker handed over into the descriptor an automation stores.
 *
 * The picker builds the CHAT descriptor, which pins the revision the file has at the moment of attaching
 * and a url addressing exactly that revision. An automation wants the opposite: it exists to repeat, and
 * every run reads the file as it is then. So both fields are dropped here rather than carried and
 * ignored — a stored revision would quietly turn the automation back into the chat's behaviour.
 */
export function automationAttachment(
  ref: ArtifactAttachmentRef,
): AutomationArtifactAttachment {
  return {
    artifact_id: ref.artifact_id,
    name: ref.name,
    content_type: ref.content_type,
    size: ref.size,
  };
}

/**
 * Turn a file picked out of a personal OneDrive into the descriptor an automation stores.
 *
 * The address the picker showed is dropped with it: the service builds its own from the metadata the
 * cloud's owner answers, and a link that came from this browser is not the one an automation should
 * carry.
 */
export function oneDriveAutomationAttachment(
  ref: OneDriveAttachmentRef,
): AutomationOneDriveAttachment {
  return {
    source: 'onedrive',
    scope_id: ref.scope_id,
    item_id: ref.item_id,
    name: ref.name,
    content_type: ref.content_type,
    size: ref.size,
  };
}

/**
 * Add a freshly picked batch to what the window already holds. Order is the order files were attached,
 * because that is the order their chips read in; a file already attached is not attached twice; and the
 * list stops at {@link MAX_AUTOMATION_ATTACHMENTS}, keeping the files picked first.
 */
export function withAutomationAttachments(
  current: readonly AutomationAttachment[],
  added: readonly AutomationAttachment[],
): AutomationAttachment[] {
  const merged = [...current];
  const attached = new Set(merged.map(automationAttachmentKey));
  for (const file of added) {
    const key = automationAttachmentKey(file);
    if (attached.has(key)) continue;
    attached.add(key);
    merged.push(file);
  }
  return merged.slice(0, MAX_AUTOMATION_ATTACHMENTS);
}

/** A stored value that names something: a non-empty string, whatever else the payload holds. */
function storedText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/** One entry of a stored `payload.attachments`, before anything is known about its shape. */
function readStoredAttachment(entry: unknown): AutomationAttachment | null {
  if (typeof entry !== 'object' || entry === null) return null;
  const stored = entry as Record<string, unknown>;
  if (stored.source === 'onedrive') return readStoredOneDrive(stored);
  const artifactId = storedText(stored.artifact_id);
  if (artifactId === null) return null;
  return {
    artifact_id: artifactId,
    name: typeof stored.name === 'string' ? stored.name : artifactId,
    content_type:
      typeof stored.content_type === 'string' ? stored.content_type : '',
    size: typeof stored.size === 'number' ? stored.size : 0,
  };
}

/**
 * One saved live reference into a personal OneDrive. Half an address points at nothing, so an entry
 * missing either the folder or the item is dropped the way a nameless artifact is.
 */
function readStoredOneDrive(
  stored: Record<string, unknown>,
): AutomationOneDriveAttachment | null {
  const scopeId = storedText(stored.scope_id);
  const itemId = storedText(stored.item_id);
  if (scopeId === null || itemId === null) return null;
  return {
    source: 'onedrive',
    scope_id: scopeId,
    item_id: itemId,
    name: typeof stored.name === 'string' ? stored.name : itemId,
    content_type:
      typeof stored.content_type === 'string' ? stored.content_type : null,
    size: typeof stored.size === 'number' ? stored.size : 0,
  };
}

/**
 * The files an automation already carries, read back out of its opaque payload.
 *
 * Everything is checked rather than cast: the payload is opaque to every service it passes through, so
 * nothing on the way here validated it, and an automation authored before this feature — or by an agent
 * tool — can put anything under that key. An entry with no `artifact_id` names no file and is dropped;
 * a `revision` somebody wrote by hand is dropped too, since the whole point is that a run reads the file
 * as it is when it fires.
 */
export function storedAutomationAttachments(
  payload: Record<string, unknown> | null | undefined,
): AutomationAttachment[] {
  const stored = payload?.attachments;
  if (!Array.isArray(stored)) return [];
  return stored
    .map(readStoredAttachment)
    .filter((file): file is AutomationAttachment => file !== null);
}

/**
 * The payload to send when the files changed: the whole stored payload, copied, with the new list
 * written over it. The scheduler treats a payload as opaque and replaces it wholesale, so a patch built
 * from the one key that changed silently drops `params`, the delivery directive and any approved tools.
 *
 * An emptied list is still written — the server has to be told the files are gone, and dropping the key
 * would read as "unchanged". An automation that never carried files gets no key at all: `attachments` is
 * meaningless on a reminder, and adding it to every payload that passes through an edit is noise.
 */
export function payloadWithAttachments(
  payload: Record<string, unknown>,
  attachments: readonly AutomationAttachment[],
): Record<string, unknown> {
  const next = { ...payload };
  if (attachments.length === 0 && !('attachments' in payload)) return next;
  next.attachments = attachments.map((file) => ({ ...file }));
  return next;
}

/**
 * What to call this file on screen. A file in someone's own OneDrive is named by the cloud that holds
 * it, and this workspace knows nothing about it, so the saved name is all there is.
 *
 * For a workspace file the descriptor keeps the name the file had when it was attached,
 * which is what an automation that has not run since still refers to — but a file renamed in the
 * workspace is the SAME file, and showing its old name makes the chip look stale or wrong. So the
 * workspace's answer wins whenever there is one, and the saved name covers the rest: the list not read
 * yet, and a file that is no longer in it.
 */
export function currentAttachmentName(
  file: AutomationAttachment,
  artifacts: readonly { id: string; name: string }[] | undefined,
): string {
  if (isOneDriveAutomationAttachment(file)) return file.name;
  const current = artifacts?.find(
    (artifact) => artifact.id === file.artifact_id,
  );
  return current?.name ?? file.name;
}
